/**
 *
 *  DayPicker
 *  DayPicker Component
 *
 *  Created by Vladimir Shislyannikov on 28/02/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React from "react";
import PropTypes from "prop-types";

import CalendarMonthGrid from "../CalendarMonthGrid/CalendarMonthGrid";
import throttle from "lodash/throttle";

// Icons
import CalendarIcon from "../../../icons/CalendarTruBeIcon";

import { availabilityCalendar as calendarConstants } from "../../../constants";

export default class DayPicker extends React.PureComponent {
  static propTypes = {
    currentMonth: PropTypes.object, // moment.js object
    getFirstFocusableDay: PropTypes.func,
    isRTL: PropTypes.bool,
    daySize: PropTypes.number,
    onKeyDown: PropTypes.func,
  };

  static defaultProps = {
    data: [],
    value: "",
    open: false,
    suggest: false,
    filter: false,
    delay: 500,

    renderCalendarInfo: () => {},
  };

  constructor(props) {
    super(props);

    this.calendarInfo = null;
    this.calendarMonthGridHeight = undefined;
    this.calendarMonthWeeks = [];
    this.container = null;
    this.throttledKeyDown = undefined;
    this.transitionContainer = null;

    let startOfMonth = props.currentMonth.clone().startOf("month");

    this.state = {
      currentMonthScrollTop: null,
      monthTransition: null,
      focusedDate: startOfMonth,
      nextFocusedDate: null,
      withMouseInteractions: true,
      calendarInfoWidth: 0,
      monthTitleHeight: 0,
      hasSetHeight: false,
      page: 0,
      monthFromNowVisible: 0, // page in somewhat
    };

    this.onKeyDown = this.onKeyDown.bind(this);
    this.throttledKeyDown = throttle(this.onFinalKeyDown, 200, {
      trailing: false,
    });

    this.setCalendarInfoRef = this.setCalendarInfoRef.bind(this);
    this.setContainerRef = this.setContainerRef.bind(this);
    this.setTransitionContainerRef = this.setTransitionContainerRef.bind(this);

    this.prevClick = this.prevClick.bind(this);
    this.nextClick = this.nextClick.bind(this);
    this.handleMonthVisibleChange = this.handleMonthVisibleChange.bind(this);
    this.handleDayVisibleChange = this.handleDayVisibleChange.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { isFocused } = this.props,
      { focusedDate } = this.state;

    var container = this.container;

    if (
      !prevProps.isFocused &&
      isFocused &&
      !focusedDate &&
      container !== null &&
      container !== undefined
    ) {
      container.focus();
    }
  }

  onKeyDown(evt) {
    evt.stopPropagation();

    if (!calendarConstants.MODIFIER_KEY_NAMES.has(evt.key)) {
      this.throttledKeyDown(evt);
    }
  }

  onFinalKeyDown(evt_t) {
    this.setState({
      withMouseInteractions: false,
    });

    const { focusedDate, nextFocusedDate } = this.state,
      _focusedDate = focusedDate || nextFocusedDate;

    if (!_focusedDate) {
      return;
    }

    const date = _focusedDate.clone();
    let transition = false;

    if (!transition) {
      this.setState({
        focusedDate: date,
        nextFocusedDate: date,
      });
    }
  }

  setContainerRef(ref) {
    this.container = ref;
  }

  setCalendarInfoRef(ref) {
    this.calendarInfo = ref;
  }

  setTransitionContainerRef(ref) {
    this.transitionContainer = ref;
  }

  prevClick() {
    this.setState({ page: this.state.page + 1 });
  }

  nextClick() {
    let page = this.state.page;

    page -= 1;

    if (page < 0) {
      page = 0;
    }

    this.setState({ page });
  }

  handleDayVisibleChange(key, inView, element, visibleItemIndexes) {
    // var maxVisibleElement =
    //   visibleItemIndexes.length && Math.max(...visibleItemIndexes);
    //
    // //if (inView && key > maxVisibleElement) {
    // if (!inView && key === 0) {
    //   console.log("onDayVisibleChange", key, inView, element);
    //   // }
    // }
    //
  }

  handleMonthVisibleChange(monthFromNowVisible) {
    this.setState({ monthFromNowVisible: monthFromNowVisible });
  }

  render() {
    const { focusedDate, page, monthFromNowVisible } = this.state;

    const {
      currentMonth,
      min,
      daySize,
      modifiers,
      dayAriaLabelFormat,
      region,
      onDayClick,
      renderCalendarDay,
    } = this.props;

    return (
      <div>
        {/*{(top || before) && renderCalendarInfo}*/}

        <div aria-hidden={true} role={"presentation"}>
          <div
            className={`daypicker-container tbk-relative tbk-mx-auto tbk-flex tbk-flex-row tbk-items-center tbk-justify-center md:tbk-max-w-[912px] md:tbk-pl-1.5 md:tbk-pr-1.5 md:tbk-pl-4 md:tbk-pr-4`}
            ref={this.setContainerRef}
            onClick={(t) => t.stopPropagation()}
            onKeyDown={this.onKeyDown}
            tabIndex={-1}
            role={"application"}
            aria-label={"Calendar"}
          >
            <div
              className="daypicker-date-label tbk-absolute tbk-hidden lg:tbk-flex lg:tbk-items-center"
              style={{
                left: "-140px",
                bottom: "24px",
              }}
            >
              <span className="tbk-mr-1 tbk-text-green">
                <CalendarIcon size={32} />
              </span>
              <div className="tbk-text-main tbk-text-blue-dark">Date:</div>
            </div>
            <div
              className={"daypicker-transition-container tbk-relative"}
              ref={this.setTransitionContainerRef}
            >
              <div
                className="daypicker-calendar-month-label tbk-text-main tbk--mb-1 tbk-text-center tbk-text-blue-dark md:tbk-mt-1 md:tbk-mt-2 md:tbk-pl-1 md:tbk-text-left"
                style={{}}
              >
                {currentMonth
                  .clone()
                  .add(monthFromNowVisible, "month")
                  .format("MMMM")}
                :
              </div>

              <CalendarMonthGrid
                page={page}
                initialMonth={currentMonth}
                min={min}
                modifiers={modifiers}
                daySize={daySize}
                focusedDate={focusedDate || undefined}
                dayAriaLabelFormat={dayAriaLabelFormat}
                region={region}
                onDayClick={onDayClick}
                onItemVisibleChange={this.handleDayVisibleChange}
                onMonthVisibleChange={this.handleMonthVisibleChange}
                renderCalendarDay={renderCalendarDay}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
